/** @file quadMaterial: the ShaderMaterial of a corner mesh (LEQUAL depth, no culling, no blending). */
import { Constants } from '@babylonjs/core/Engines/constants.js';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial.js';
import type { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import { QUAD_FRAGMENT_GLSL } from '@zilo/pose3d-runtime-babylon/draw/quadFragmentGlsl';
import { QUAD_VERTEX_GLSL } from '@zilo/pose3d-runtime-babylon/draw/quadVertexGlsl';

/**
 * `QUAD_VERTEX_GLSL` + `QUAD_FRAGMENT_GLSL` filling each quad as raster does (coverage, cut, depth, texel; nearest or
 * bilinear by profile; `drawSet` sets `uHeight`, the target height in pixels); depth function `LEQUAL` so a later quad wins a depth
 * tie exactly as raster's painter does; back faces drawn (mirrored quads keep their winding on screen).
 */
export function quadMaterial(scene: Scene, sheet: RawTexture, sampling: Profile['sampling'], name: string): ShaderMaterial {
  const material = new ShaderMaterial(name, scene, { vertexSource: QUAD_VERTEX_GLSL, fragmentSource: QUAD_FRAGMENT_GLSL }, {
    attributes: ['position', 'geomA', 'geomB', 'depth3', 'cut3', 'texRect'], uniforms: ['worldViewProjection', 'nearest', 'uHeight'], samplers: ['sheet'],
  });
  material.setTexture('sheet', sheet);
  material.setFloat('nearest', sampling === 'nearest' ? 1 : 0);
  material.backFaceCulling = false;
  material.depthFunction = Constants.LEQUAL;
  return material;
}
